<template>
  <div class="review-queue">
    <div class="header">
      <h1>Review Queue</h1>
      <p class="subtitle">Transactions matched with Monarch that need categorization</p>
    </div>

    <div v-if="loading" class="loading">Loading...</div>

    <div v-else-if="transactions.length === 0" class="empty-state">
      <div class="empty-icon">&#10003;</div>
      <h2>All caught up!</h2>
      <p>No transactions need review right now.</p>
      <router-link to="/transactions" class="btn-secondary">View All Transactions</router-link>
    </div>

    <div v-else class="queue-content">
      <div class="queue-summary">
        <div class="summary-card">
          <span class="summary-count">{{ transactions.length }}</span>
          <span class="summary-label">transactions to review</span>
        </div>
        <button @click="startReview" class="btn-start">
          Start Review &rarr;
        </button>
      </div>

      <div class="queue-list">
        <h2>Queue</h2>
        <div class="transaction-list">
          <div
            v-for="(txn, index) in transactions"
            :key="txn.id"
            class="transaction-row"
            @click="goToTransaction(txn.id)"
          >
            <span class="row-number">{{ index + 1 }}</span>
            <div class="row-info">
              <span class="order-number">
                {{ txn.order_number ? `#${txn.order_number}` : 'Order' }}
              </span>
              <span class="order-date">{{ formatDate(txn.order_date) }}</span>
            </div>
            <span class="order-amount">{{ formatAmount(txn.order_total) }}</span>
            <span class="item-count">{{ txn.items?.length || 0 }} items</span>
            <span class="arrow">&rarr;</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { transactionsAPI } from '../services/api'

const router = useRouter()
const loading = ref(true)
const transactions = ref([])

onMounted(async () => {
  await loadQueue()
})

const loadQueue = async () => {
  try {
    loading.value = true
    const response = await transactionsAPI.getReviewQueue()
    transactions.value = response.data.transactions || []
  } catch (error) {
    console.error('Error loading review queue:', error)
  } finally {
    loading.value = false
  }
}

const startReview = () => {
  if (transactions.value.length > 0) {
    router.push(`/transactions/${transactions.value[0].id}?review=true`)
  }
}

const goToTransaction = (id) => {
  router.push(`/transactions/${id}?review=true`)
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Date unknown'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '$—'
  return `$${Number(amount).toFixed(2)}`
}
</script>

<style scoped>
.review-queue {
  max-width: 800px;
  margin: 0 auto;
}

.header {
  margin-bottom: 2rem;
}

h1 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #7f8c8d;
  font-size: 1rem;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  font-size: 4rem;
  color: #27ae60;
  margin-bottom: 1rem;
}

.empty-state h2 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #7f8c8d;
  margin-bottom: 1.5rem;
}

.btn-secondary {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 2px solid #3498db;
  color: #3498db;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #3498db;
  color: white;
}

.queue-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.queue-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.summary-card {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.summary-count {
  font-size: 3rem;
  font-weight: 700;
  color: #e67e22;
}

.summary-label {
  font-size: 1.1rem;
  color: #7f8c8d;
}

.btn-start {
  background: #27ae60;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-start:hover {
  background: #229954;
}

.queue-list {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.queue-list h2 {
  color: #2c3e50;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #ecf0f1;
}

.transaction-list {
  display: flex;
  flex-direction: column;
}

.transaction-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #ecf0f1;
  cursor: pointer;
  transition: background-color 0.15s;
}

.transaction-row:last-child {
  border-bottom: none;
}

.transaction-row:hover {
  background-color: #f8f9fa;
}

.row-number {
  width: 28px;
  height: 28px;
  background: #ecf0f1;
  color: #7f8c8d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
}

.row-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.order-number {
  font-weight: 600;
  color: #2c3e50;
}

.order-date {
  font-size: 0.85rem;
  color: #7f8c8d;
}

.order-amount {
  font-weight: 600;
  color: #27ae60;
  min-width: 80px;
  text-align: right;
}

.item-count {
  font-size: 0.85rem;
  color: #7f8c8d;
  min-width: 60px;
}

.arrow {
  color: #bdc3c7;
  font-size: 1.2rem;
}

.transaction-row:hover .arrow {
  color: #3498db;
}
</style>
